import { Wand2, FolderInput, Receipt, CheckSquare, Wallet, PieChart } from 'lucide-react';
import { CBLogo } from '../CBLogo';
import { AnthropicMark, OpenAIMark, GeminiMark, MetaMark, PerplexityMark } from '../landing/BrandLogos';

/**
 * AIHub empty-state hero — shown when no thread is selected or the current
 * thread has zero messages. Starter prompts are one-click: they auto-create
 * a thread and send the message via `onPrompt` so new signups see the
 * agentic AI in action within their first minute.
 */

const STARTERS = [
  {
    icon: Wand2,
    label: 'Categorize my transactions',
    sub: 'Reviews everything uncategorized, proposes categories, posts on your approval',
    prompt: 'Please categorize my uncategorized bank transactions.',
    action: true,
  },
  {
    icon: Receipt,
    label: 'Draft an invoice',
    sub: 'Tell me the customer and amount — I create the draft for you',
    prompt: 'I want to create a draft invoice. Ask me for the customer and line items, then create it.',
    action: true,
  },
  {
    icon: FolderInput,
    label: 'File a receipt or document',
    sub: 'Attach a file with the paperclip and I store it in the right folder',
    prompt: 'I have a receipt to file into my documents. What folders do I have, and how do I attach it so you can store it for me?',
    action: true,
  },
  {
    icon: CheckSquare,
    label: 'Add a task for me',
    sub: 'Straight onto your task board',
    prompt: 'Create a task reminding me to review my overdue invoices tomorrow — high priority.',
    action: true,
  },
  {
    icon: Wallet,
    label: 'How much cash do I have?',
    sub: 'Live answer from your accounts',
    prompt: 'How much cash do I have right now?',
    action: false,
  },
  {
    icon: PieChart,
    label: 'Where is my money going?',
    sub: 'Top expense categories, real numbers',
    prompt: 'What were my top expense categories over the last 90 days?',
    action: false,
  },
];

export function EmptyState({ onPrompt }) {
  const marks = [
    { Mark: AnthropicMark, name: 'Claude' },
    { Mark: OpenAIMark, name: 'GPT-4o' },
    { Mark: GeminiMark, name: 'Gemini' },
    { Mark: MetaMark, name: 'Llama' },
    { Mark: PerplexityMark, name: 'Perplexity' },
  ];
  return (
    <div className="text-center py-12 relative" data-testid="ai-hub-empty">
      <p className="text-[11px] font-mono uppercase tracking-[0.28em] text-zinc-500 dark:text-zinc-600 mb-5">
        AI Studio · Powered by you
      </p>
      <div className="inline-flex items-center justify-center w-20 h-20 mb-5">
        <CBLogo size="2xl" />
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter text-zinc-900 dark:text-white mb-3 leading-[1.05]">
        Don't just ask.<br className="sm:hidden" />
        <span className="bg-gradient-to-r from-emerald-500 via-violet-500 to-blue-500 dark:from-emerald-400 dark:via-violet-400 dark:to-blue-400 bg-clip-text text-transparent"> Delegate.</span>
      </h2>
      <p className="text-zinc-600 dark:text-zinc-400 text-sm mb-8 max-w-md mx-auto leading-relaxed">
        This AI works inside your books — it categorizes transactions, files documents,
        drafts invoices and creates tasks. Anything that touches your ledger waits for your approval.
      </p>

      {/* Brand marks row */}
      <div className="flex items-center justify-center gap-6 sm:gap-8 mb-10 flex-wrap">
        {marks.map(({ Mark, name }) => (
          <div key={name} className="flex flex-col items-center gap-1.5 opacity-70 hover:opacity-100 transition-opacity">
            <Mark className="w-6 h-6" />
            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 dark:text-zinc-600">{name}</span>
          </div>
        ))}
      </div>

      <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-400 dark:text-zinc-700 mb-3">
        Try one — it really does it
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl mx-auto" data-testid="ai-hub-suggestions">
        {STARTERS.map((s, i) => {
          const Icon = s.icon;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onPrompt?.(s.prompt)}
              className="group text-left px-4 py-3 rounded-xl border border-zinc-200 dark:border-white/10 bg-white/60 dark:bg-zinc-950/60 backdrop-blur hover:border-emerald-500/40 hover:bg-white/80 dark:hover:bg-zinc-900/80 transition-all cursor-pointer"
              data-testid={`ai-hub-starter-${i}`}
            >
              <div className="flex items-center gap-2 mb-0.5">
                <Icon className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 group-hover:text-zinc-900 dark:group-hover:text-white">
                  {s.label}
                </span>
                {s.action && (
                  <span className="ml-auto px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-[9px] font-mono uppercase tracking-wider flex-shrink-0">
                    Does it for you
                  </span>
                )}
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-500 leading-relaxed pl-6">{s.sub}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default EmptyState;
